import type { DirectoryDto, FileContentDto, GitStatusDto, PreviewDto, SandboxStatusDto, TrashItemDto, WorkspaceDto } from '@dbrain/shared';
import { Alert, Button, EmptyState, Field, Icons, Spinner, TabPanel, Tabs, TextInput } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, isApiError, upload } from '../../api/client.js';
import { useMeta } from '../../experience.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { reloadNow } from './preview-frame.js';

/**
 * The code workspace's environment (06 §15): files, terminal, git and
 * preview of the caller's own sandbox. Chat spaces never show this panel;
 * their sandbox stays hidden (SA-068, D-35). Next to a conversation the
 * preview opens when the agent starts a server and reloads when it changes
 * files (D-06); a file named in an answer or "Önizlemede aç" asks for its
 * tab (D-99).
 */
const TerminalTab = lazy(() => import('./TerminalTab.js'));

type Tab = 'files' | 'terminal' | 'git' | 'preview';

export const sandboxKey = (id: string) => ['workspace', id, 'sandbox'] as const;
/** the workspace's top folder; the files tab shares the cache */
export const filesKey = (id: string, path: string) => ['workspace', id, 'files', path] as const;

/** The caller's sandbox of a code workspace: missing, running, idle or suspended (reading it never starts it). */
export function useSandboxStatus(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: sandboxKey(workspaceId),
    queryFn: () => api<SandboxStatusDto>('GET', `/api/workspaces/${workspaceId}/sandbox`),
    enabled: enabled && Boolean(workspaceId),
  });
}

export interface PreviewRequest {
  port: number;
  nonce: number;
}

/** A click in the conversation that needs the panel, on its tab (D-99). */
export type PanelRequest = { tab: 'files'; path: string; nonce: number } | { tab: 'preview'; port: number; nonce: number };

export function SandboxPanel({
  workspace,
  preview = null,
  filesVersion = 0,
  request = null,
}: {
  workspace: WorkspaceDto;
  preview?: PreviewRequest | null;
  filesVersion?: number;
  request?: PanelRequest | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('files');
  // the port the preview shows: a server the agent started, or one the user asked for
  const [target, setTarget] = useState<PreviewRequest | null>(preview);
  // D-06: a server the agent started opens the preview
  useEffect(() => {
    if (!preview) return;
    setTab('preview');
    setTarget(preview);
    void queryClient.invalidateQueries({ queryKey: sandboxKey(workspace.id) });
  }, [preview, queryClient, workspace.id]);
  // D-99: the user asked for a file or a preview from the conversation
  useEffect(() => {
    if (!request) return;
    setTab(request.tab);
    if (request.tab === 'preview') setTarget({ port: request.port, nonce: request.nonce });
  }, [request]);
  // the agent changed files: the file list and git status are stale
  useEffect(() => {
    if (filesVersion > 0) void queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] });
  }, [filesVersion, queryClient, workspace.id]);
  const status = useSandboxStatus(workspace.id);
  const start = useMutation({
    mutationFn: () => api<SandboxStatusDto>('POST', `/api/workspaces/${workspace.id}/sandbox/start`),
    onSuccess: (data) => queryClient.setQueryData(sandboxKey(workspace.id), data),
  });
  const suspend = useMutation({
    mutationFn: () => api<SandboxStatusDto>('POST', `/api/workspaces/${workspace.id}/sandbox/suspend`),
    onSuccess: (data) => queryClient.setQueryData(sandboxKey(workspace.id), data),
  });
  const state = status.data?.state ?? 'missing';
  const active = state === 'running' || state === 'idle';

  const tabsId = `sandbox-${workspace.id}`;
  return (
    <section className="env-panel" aria-label={t('sandbox.title')} data-testid="sandbox-panel">
      <header className="env-panel__head">
        {active ? (
          <Tabs
            tabsId={tabsId}
            label={t('sandbox.tabsLabel')}
            value={tab}
            onChange={setTab}
            tabs={(['files', 'terminal', 'git', 'preview'] as Tab[]).map((name) => ({ id: name, label: t(`sandbox.tabs.${name}`), 'data-testid': `sandbox-tab-${name}` }))}
          />
        ) : (
          <strong className="env-panel__title">{t('sandbox.title')}</strong>
        )}
        <div className="env-panel__state">
          <span className={active ? 'env-state env-state--on' : 'env-state'}>
            <span className={active ? 'ui-dot ui-dot--success' : 'ui-dot'} aria-hidden="true" />
            <span data-testid="sandbox-state">{t(`sandbox.states.${state}`)}</span>
          </span>
          {active ? (
            <Button size="sm" variant="ghost" busy={suspend.isPending} onClick={() => suspend.mutate()}>
              {t('sandbox.suspend')}
            </Button>
          ) : null}
        </div>
      </header>
      <ErrorAlert error={status.error ?? start.error ?? suspend.error} />
      {active ? (
        <TabPanel tabsId={tabsId} id={tab} className="env-panel__body">
          {tab === 'files' ? <FilesTab workspaceId={workspace.id} request={request?.tab === 'files' ? request : null} /> : null}
          {tab === 'terminal' ? (
            <Suspense fallback={<Spinner label={t('common.loading')} />}>
              <TerminalTab workspaceId={workspace.id} />
            </Suspense>
          ) : null}
          {tab === 'git' ? <GitTab workspaceId={workspace.id} /> : null}
          {tab === 'preview' ? <PreviewTab workspaceId={workspace.id} request={target} filesVersion={filesVersion} onFiles={() => setTab('files')} /> : null}
        </TabPanel>
      ) : (
        <EmptyState
          icon={<Icons.terminal />}
          title={t('sandbox.offTitle')}
          action={
            <Button busy={start.isPending} onClick={() => start.mutate()} data-testid="sandbox-start">
              {t('sandbox.start')}
            </Button>
          }
        >
          {t('sandbox.isolated')}
        </EmptyState>
      )}
    </section>
  );
}

// Files (SA-014) --------------------------------------------------------------------

function FilesTab({ workspaceId, request }: { workspaceId: string; request: Extract<PanelRequest, { tab: 'files' }> | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [path, setPath] = useState('.');
  const [open, setOpen] = useState<string | null>(null);
  const [showTrash, setShowTrash] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const base = `/api/workspaces/${workspaceId}`;
  const listKey = filesKey(workspaceId, path);
  // D-99: a file named in an answer opens with its folder; "." is the top folder itself
  useEffect(() => {
    if (!request) return;
    if (request.path === '.') {
      setPath('.');
      setOpen(null);
      return;
    }
    const at = request.path.lastIndexOf('/');
    setPath(at > 0 ? request.path.slice(0, at) : '.');
    setOpen(request.path);
  }, [request]);
  const listing = useQuery({ queryKey: listKey, queryFn: () => api<DirectoryDto>('GET', `${base}/files?path=${encodeURIComponent(path)}`) });
  const file = useQuery({
    queryKey: ['workspace', workspaceId, 'file', open],
    queryFn: () => api<FileContentDto>('GET', `${base}/files/content?path=${encodeURIComponent(open as string)}`),
    enabled: open !== null,
  });
  const trash = useQuery({
    queryKey: ['workspace', workspaceId, 'trash'],
    queryFn: () => api<TrashItemDto[]>('GET', `${base}/trash`),
    enabled: showTrash,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
  const remove = useMutation({ mutationFn: (target: string) => api('DELETE', `${base}/files?path=${encodeURIComponent(target)}`), onSuccess: refresh });
  const restore = useMutation({ mutationFn: (id: string) => api('POST', `${base}/trash/${id}/restore`), onSuccess: refresh });
  const send = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await upload('PUT', `${base}/files/upload?path=${encodeURIComponent(join(path, f.name))}`, new Blob([f], { type: 'application/octet-stream' }));
    },
    onSuccess: refresh,
  });
  const segments = path === '.' ? [] : path.split('/');

  return (
    <div data-testid="files-tab">
      <div className="toolbar">
        <Button size="sm" variant="ghost" onClick={() => setPath('.')}>
          {t('sandbox.files.root')}
        </Button>
        {segments.map((segment, index) => (
          <Button key={index} size="sm" variant="ghost" onClick={() => setPath(segments.slice(0, index + 1).join('/'))}>
            / {segment}
          </Button>
        ))}
        <span className="spacer" />
        <input
          ref={input}
          type="file"
          multiple
          className="visually-hidden"
          aria-label={t('sandbox.files.upload')}
          data-testid="files-upload-input"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            if (files.length > 0) send.mutate(files);
          }}
        />
        <Button size="sm" variant="secondary" busy={send.isPending} onClick={() => input.current?.click()}>
          {t('sandbox.files.upload')}
        </Button>
        <a className="ui-button ui-button--secondary ui-button--sm" href={`${base}/zip?path=${encodeURIComponent(path)}`} download>
          {t('sandbox.files.zip')}
        </a>
        <Button size="sm" variant="ghost" onClick={() => setShowTrash(!showTrash)}>
          {t('sandbox.files.trash')}
        </Button>
      </div>
      <ErrorAlert error={listing.error ?? remove.error ?? restore.error ?? send.error} />
      {listing.data?.entries.length === 0 ? <EmptyState>{t('sandbox.files.empty')}</EmptyState> : null}
      <ul className="file-list" data-testid="file-list">
        {path !== '.' ? (
          <li>
            <button className="link" onClick={() => setPath(segments.slice(0, -1).join('/') || '.')}>
              .. ({t('sandbox.files.up')})
            </button>
          </li>
        ) : null}
        {listing.data?.entries.map((entry) => {
          const target = join(path, entry.name);
          return (
            <li key={entry.name}>
              <button className="link" onClick={() => (entry.type === 'directory' ? setPath(target) : setOpen(target))}>
                {entry.type === 'directory' ? `📁 ${entry.name}` : entry.name}
              </button>
              <span className="row">
                {entry.type !== 'directory' ? (
                  <a className="muted" href={`${base}/files/download?path=${encodeURIComponent(target)}`} download={entry.name}>
                    {t('sandbox.files.download')}
                  </a>
                ) : null}
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(target)}>
                  {t('sandbox.files.delete')}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
      {listing.data && listing.data.hiddenIgnored > 0 ? <p className="muted">{t('sandbox.files.hiddenIgnored', { count: listing.data.hiddenIgnored })}</p> : null}
      {open ? (
        <div className="file-view">
          <div className="toolbar">
            <strong>{open}</strong>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
              {t('sandbox.files.close')}
            </Button>
          </div>
          <ErrorAlert error={file.error} />
          {file.data?.truncated ? <Alert tone="info" title={t('sandbox.files.truncated')} /> : null}
          {file.data ? <pre className="code-block" data-testid="file-content">{file.data.content}</pre> : null}
        </div>
      ) : null}
      {showTrash ? (
        <div>
          <h3>{t('sandbox.files.trash')}</h3>
          {trash.data?.length === 0 ? <EmptyState>{t('sandbox.files.trashEmpty')}</EmptyState> : null}
          <ul className="file-list">
            {trash.data?.map((item) => (
              <li key={item.id}>
                <span>{item.path}</span>
                <Button size="sm" variant="secondary" onClick={() => restore.mutate(item.id)}>
                  {t('sandbox.files.restore')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function join(dir: string, name: string): string {
  return dir === '.' ? name : `${dir}/${name}`;
}

// Git (QW-712, QW-713) -------------------------------------------------------------------

function GitTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const base = `/api/workspaces/${workspaceId}`;
  const [selected, setSelected] = useState<string[] | null>(null);
  const [message, setMessage] = useState('');
  const [diffPath, setDiffPath] = useState<string | null>(null);
  const status = useQuery({ queryKey: ['workspace', workspaceId, 'git'], queryFn: () => api<GitStatusDto>('GET', `${base}/git/status`) });
  const diff = useQuery({
    queryKey: ['workspace', workspaceId, 'git', 'diff', diffPath],
    queryFn: () => api<{ patch: string }>('GET', `${base}/git/diff?path=${encodeURIComponent(diffPath as string)}`),
    enabled: diffPath !== null,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId, 'git'] });
  const init = useMutation({ mutationFn: () => api('POST', `${base}/git/init`), onSuccess: refresh });
  const files = status.data?.files ?? [];
  const chosen = selected ?? files.map((file) => file.path);
  const commit = useMutation({
    mutationFn: () => api('POST', `${base}/git/commit`, { message, paths: chosen }),
    onSuccess: () => {
      setMessage('');
      setSelected(null);
      refresh();
    },
  });

  if (status.isPending) return <Spinner label={t('common.loading')} />;
  if (status.data && !status.data.isRepo) {
    return (
      <div>
        <p className="muted">{t('sandbox.git.notRepo')}</p>
        <ErrorAlert error={init.error} />
        <Button busy={init.isPending} onClick={() => init.mutate()}>
          {t('sandbox.git.init')}
        </Button>
      </div>
    );
  }
  return (
    <div data-testid="git-tab">
      <ErrorAlert error={status.error ?? commit.error} />
      {status.data?.branch ? <p className="muted">{t('sandbox.git.branch', { branch: status.data.branch })}</p> : null}
      {commit.isSuccess ? <Alert tone="success" title={t('sandbox.git.committed')} /> : null}
      {files.length === 0 ? <EmptyState>{t('sandbox.git.clean')}</EmptyState> : null}
      <ul className="file-list">
        {files.map((file) => (
          <li key={file.path}>
            <label className="row">
              <input
                type="checkbox"
                checked={chosen.includes(file.path)}
                onChange={(e) => setSelected(e.target.checked ? [...chosen, file.path] : chosen.filter((p) => p !== file.path))}
              />
              <code>{file.untracked ? '??' : `${file.staged}${file.unstaged}`}</code> {file.path}
            </label>
            <Button size="sm" variant="ghost" onClick={() => setDiffPath(file.path)}>
              {t('sandbox.git.diff')}
            </Button>
          </li>
        ))}
      </ul>
      {diffPath && diff.data ? <pre className="code-block">{diff.data.patch}</pre> : null}
      {files.length > 0 ? (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (message.trim() && chosen.length > 0) commit.mutate();
          }}
        >
          <Field label={t('sandbox.git.message')}>
            {({ id, describedBy }) => (
              <TextInput id={id} aria-describedby={describedBy} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="git-message" />
            )}
          </Field>
          <Button type="submit" busy={commit.isPending} disabled={!message.trim() || chosen.length === 0}>
            {t('sandbox.git.commit')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

// Preview (QW-710, SA-013) ------------------------------------------------------------------

/** A preview address as a browser reads it; null when it cannot (v0.15a.3: never an error while drawing). */
function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** v0.15a.3: an installation reached by its IP address has no previews; it says so and points to the files. */
function PreviewTab({ workspaceId, request, filesVersion, onFiles }: { workspaceId: string; request: PreviewRequest | null; filesVersion: number; onFiles: () => void }) {
  const off = useMeta().data?.previews === false;
  const [unavailable, setUnavailable] = useState(false);
  if (off || unavailable) return <PreviewsOff onFiles={onFiles} />;
  return <PreviewFrame workspaceId={workspaceId} request={request} filesVersion={filesVersion} onUnavailable={() => setUnavailable(true)} />;
}

function PreviewsOff({ onFiles }: { onFiles: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="preview-tab" data-testid="preview-tab">
      <EmptyState
        icon={<Icons.monitor />}
        title={t('sandbox.preview.offTitle')}
        action={
          <Button size="sm" variant="secondary" onClick={onFiles} data-testid="preview-off-files">
            {t('sandbox.preview.offFiles')}
          </Button>
        }
        data-testid="preview-off"
      >
        {t('sandbox.preview.offBody')}
      </EmptyState>
    </div>
  );
}

function PreviewFrame({ workspaceId, request, filesVersion, onUnavailable }: { workspaceId: string; request: PreviewRequest | null; filesVersion: number; onUnavailable: () => void }) {
  const { t } = useTranslation();
  const base = `/api/workspaces/${workspaceId}`;
  const [frame, setFrame] = useState<{ url: string; nonce: number } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const ports = useQuery({ queryKey: ['workspace', workspaceId, 'ports'], queryFn: () => api<{ ports: number[] }>('GET', `${base}/ports`) });
  // an address the page cannot read, or an API that says previews are off, shows the note instead
  const show = (url: string, how: (url: string) => void) => (originOf(url) ? how(url) : onUnavailable());
  const refused = (error: unknown) => {
    if (isApiError(error, 'preview_unavailable')) onUnavailable();
  };
  const open = useMutation({
    mutationFn: (port: number) => api<PreviewDto>('POST', `${base}/previews`, { port }),
    onSuccess: (preview) => show(preview.url, (url) => setFrame({ url, nonce: Date.now() })),
    onError: refused,
  });
  const newTab = useMutation({
    mutationFn: (port: number) => api<PreviewDto>('POST', `${base}/previews`, { port }),
    onSuccess: (preview) => show(preview.url, (url) => window.open(url, '_blank', 'noopener')),
    onError: refused,
  });
  const origin = frame ? originOf(frame.url) : null;
  const openPort = open.mutate;
  const handled = useRef<number | null>(null);
  useEffect(() => {
    if (!request || handled.current === request.nonce) return;
    handled.current = request.nonce;
    openPort(request.port);
  }, [request, openPort]);
  // 16c.5 (D-404): a frame still signing in (its one-time link) is not replaced; the reload waits for its load
  const loaded = useRef<number | null>(null);
  const waiting = useRef(false);
  const reload = () => {
    if (!frame || !origin) return;
    if (reloadNow(frame, loaded.current)) setFrame({ url: `${origin}/`, nonce: Date.now() });
    else waiting.current = true;
  };
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const onLoad = (nonce: number) => {
    loaded.current = nonce;
    if (!waiting.current) return;
    waiting.current = false;
    reloadRef.current();
  };
  const seenVersion = useRef(filesVersion);
  useEffect(() => {
    if (filesVersion === seenVersion.current) return;
    seenVersion.current = filesVersion;
    if (autoRefresh) reloadRef.current();
  }, [filesVersion, autoRefresh]);
  return (
    <div className="preview-tab" data-testid="preview-tab">
      <div className="toolbar">
        <strong>{t('sandbox.preview.ports')}</strong>
        <Button size="sm" variant="ghost" busy={ports.isFetching} onClick={() => void ports.refetch()}>
          {t('sandbox.preview.refresh')}
        </Button>
      </div>
      <ErrorAlert error={ports.error ?? open.error ?? newTab.error} />
      {ports.data?.ports.length === 0 ? <EmptyState>{t('sandbox.preview.noPorts')}</EmptyState> : null}
      <ul className="file-list">
        {ports.data?.ports.map((port) => (
          <li key={port}>
            <code>localhost:{port}</code>
            <span className="row">
              <Button size="sm" busy={open.isPending} onClick={() => open.mutate(port)} data-testid={`preview-open-${port}`}>
                {t('sandbox.preview.open')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => newTab.mutate(port)} data-testid={`preview-new-tab-${port}`}>
                {t('sandbox.preview.newTab')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
      {frame && origin ? (
        <>
          <div className="toolbar">
            <code>{origin}</code>
            <Button size="sm" variant="ghost" onClick={reload} data-testid="preview-reload">
              {t('sandbox.preview.reload')}
            </Button>
            <label className="row">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="preview-auto-refresh" />
              {t('agent.preview.autoRefresh')}
            </label>
          </div>
          <iframe
            key={frame.nonce}
            className="preview-frame"
            src={frame.url}
            title={t('sandbox.preview.frameTitle')}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            onLoad={() => onLoad(frame.nonce)}
            data-testid="preview-frame"
            data-nonce={frame.nonce}
          />
        </>
      ) : null}
    </div>
  );
}
